import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ActivateUserRequest, LoginResponse, PermisoEnum, RoleEnum, WrapperResponse } from '../model/kartera-api-model'
import * as Roles from '../utils/Roles'
import APIInvoke, { setUnauthorizedHandler } from '../utils/APIInvoke'
import UserContext from '../state/UserContext'
import { mapLoginUser } from './mapLoginUser'
import type { SessionUser } from './mapLoginUser'
import { clearToken, getToken, isTokenExpired, setToken, shouldRemember } from './session'

export interface LoginCredentials {
    usuario: string
    password: string
    remember?: boolean
}

export interface AuthContextValue {
    user: SessionUser | null
    setUser: React.Dispatch<React.SetStateAction<SessionUser | null>>
    isReady: boolean
    isAuthenticated: boolean
    login: (credentials: LoginCredentials) => Promise<SessionUser | null>
    activate: (request: ActivateUserRequest) => Promise<SessionUser | null>
    logout: () => void
    restoreSession: () => Promise<SessionUser | null>
    restorePassword: (email: string) => Promise<WrapperResponse<unknown>>
    canAccess: (permisoOrList: PermisoEnum | PermisoEnum[]) => boolean
    hasRole: (role: RoleEnum) => boolean
    hasPermiso: (permiso: PermisoEnum) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

const readJson = async <T,>(response: Response): Promise<WrapperResponse<T> | null> => {
    try {
        return await response.json()
    } catch (error) {
        return null
    }
}

const applySession = (body: LoginResponse | null, remember: boolean) => {
    if (!body?.token) return null
    setToken(body.token, remember)
    return mapLoginUser(body)
}

export const AuthProvider = ({ children }: { children?: React.ReactNode }) => {
    const [user, setUser] = useState<SessionUser | null>(null)
    const [isReady, setReady] = useState(false)

    const logout = useCallback(() => {
        clearToken()
        setUser(null)
    }, [])

    const restoreSession = useCallback(async () => {
        const token = getToken()
        if (!token || isTokenExpired(token)) {
            clearToken()
            setUser(null)
            setReady(true)
            return null
        }

        try {
            const response = await APIInvoke.invokePOST('/usuarios/login')
            const payload = await readJson<LoginResponse>(response)
            if (payload?.ok) {
                const nextUser = applySession(payload.body, shouldRemember())
                setUser(nextUser)
                setReady(true)
                return nextUser
            }
            logout()
            setReady(true)
            return null
        } catch (error) {
            setReady(true)
            return null
        }
    }, [logout])

    useEffect(() => {
        restoreSession()
    }, [restoreSession])

    useEffect(() => {
        setUnauthorizedHandler(() => {
            clearToken()
            setUser(null)
        })
        return () => setUnauthorizedHandler(null)
    }, [])

    const login = useCallback(async ({ usuario, password, remember = true }: LoginCredentials) => {
        const response = await APIInvoke.invokePOST('/usuarios/login', { usuario, password })
        const payload = await readJson<LoginResponse>(response)
        if (!payload) {
            throw new Error('No pudimos conectar con el servidor. Inténtalo de nuevo.')
        }
        if (!payload.ok) {
            throw new Error(payload.message || 'Correo o contraseña incorrectos. Revisa tus datos e inténtalo de nuevo.')
        }
        const nextUser = applySession(payload.body, remember)
        setUser(nextUser)
        return nextUser
    }, [])

    const activate = useCallback(async (request: ActivateUserRequest) => {
        const response = await APIInvoke.invokePOST('/usuarios/activate', request)
        const payload = await readJson<LoginResponse>(response)
        if (!payload) {
            throw new Error('No pudimos conectar con el servidor. Inténtalo de nuevo.')
        }
        if (!payload.ok) {
            throw new Error(payload.message || 'No pudimos activar tu cuenta. Revisa el código e inténtalo de nuevo.')
        }
        const nextUser = applySession(payload.body, true)
        setUser(nextUser)
        return nextUser
    }, [])

    const restorePassword = useCallback(async (email: string) => {
        const response = await APIInvoke.invokePUT('/usuarios/password/restore', { email })
        const payload = await readJson<unknown>(response)
        if (!payload) {
            throw new Error('No pudimos conectar con el servidor. Inténtalo de nuevo.')
        }
        return payload
    }, [])

    const hasRole = useCallback((role: RoleEnum) => (user?.rol || []).includes(role), [user])

    const hasPermiso = useCallback((permiso: PermisoEnum) => (user?.permisos || []).includes(permiso), [user])

    const canAccess = useCallback((permisoOrList: PermisoEnum | PermisoEnum[]) => {
        if (!user) return false
        if (hasRole(Roles.ROLE_ADMIN)) return true
        const list = Array.isArray(permisoOrList) ? permisoOrList : [permisoOrList]
        return list.some((permiso) => (user.permisos || []).includes(permiso))
    }, [hasRole, user])

    const value = useMemo<AuthContextValue>(() => ({
        user,
        setUser,
        isReady,
        isAuthenticated: Boolean(user),
        login,
        activate,
        logout,
        restoreSession,
        restorePassword,
        canAccess,
        hasRole,
        hasPermiso,
    }), [user, isReady, login, activate, logout, restoreSession, restorePassword, canAccess, hasRole, hasPermiso])

    return (
        <AuthContext.Provider value={value}>
            <UserContext.Provider value={{ user, setUser }}>
                {children}
            </UserContext.Provider>
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth debe usarse dentro de AuthProvider')
    }
    return context
}

export default AuthContext
